/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { cssMap, jsx } from '@atlaskit/css';
import { MediaImage } from '@atlaskit/media-image';
import type { MediaClientConfig, Auth as MediaAuth, AuthProvider } from '@atlaskit/media-core';
import type { UploadRejectionData } from '@atlaskit/media-picker';

import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';

import type { CoverPictureId } from '../pageCoverPictureTypes.ts';

import { ImageWrapper } from './ImageWrapper.tsx';

const styles = cssMap({
	loading: {
		position: 'relative',
		height: '100%',
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingDivWithCss = styled.div<{
	dynamicCoverImageHeight?: number;
	smartSpaceOverviewHeight?: number;
}>({
	position: 'relative',
	height: '100%',
	background: token('color.background.accent.gray.subtlest'),
	borderRadius: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: (props) => `${props.smartSpaceOverviewHeight ?? props.dynamicCoverImageHeight}px`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: (props) => `-${props.smartSpaceOverviewHeight ?? props.dynamicCoverImageHeight}px`,
});

const LoadingDiv = ({
	dynamicCoverImageHeight,
	smartSpaceOverviewHeight,
}: {
	dynamicCoverImageHeight?: number;
	smartSpaceOverviewHeight?: number;
}) => {
	if (expValEquals('confluence_page_cover_picture_css_migration', 'isEnabled', true)) {
		return (
			<div
				css={styles.loading}
				style={{
					minHeight: smartSpaceOverviewHeight
						? `${smartSpaceOverviewHeight}px`
						: dynamicCoverImageHeight
							? `${dynamicCoverImageHeight}px`
							: undefined,
					marginBottom: smartSpaceOverviewHeight
						? `-${smartSpaceOverviewHeight}px`
						: dynamicCoverImageHeight
							? `-${dynamicCoverImageHeight}px`
							: undefined,
				}}
				data-testid="cover-pic-loading-css" // TODO: rename to cover-pic-loading when cleaning up confluence_page_cover_picture_css_migration
			/>
		);
	}
	return (
		<LoadingDivWithCss
			data-testid="cover-pic-loading"
			dynamicCoverImageHeight={dynamicCoverImageHeight}
			smartSpaceOverviewHeight={smartSpaceOverviewHeight}
		/>
	);
};

export type MediaCoverPictureProps = {
	showErrorFlag: () => void;
	coverPicture: React.MutableRefObject<{ draft: CoverPictureId | null }>;
	mediaConfig: {
		collection: string | undefined;
		authProvider: MediaClientConfig['authProvider'];
		onUploadRejection?: (data: UploadRejectionData) => boolean;
	} | null;
	dynamicCoverImageHeight?: number;
	ssrConfig:
		| {
				mode: 'server' | 'client';
				config: {
					authProvider: AuthProvider;
					initialAuth?: MediaAuth;
					enforceDataSecurityPolicy?: boolean;
				};
		  }
		| undefined;
	contentId: string;
	hasSSRConfigInSSRModeOrHydration?: boolean;
	isFixedWidthImageOption?: boolean;
	smartSpaceOverviewHeight?: number;
	isAIGenerated?: boolean;
	isRoundedCorners?: boolean;
};

export const MediaCoverPicture = ({
	coverPicture,
	dynamicCoverImageHeight,
	showErrorFlag,
	mediaConfig,
	ssrConfig,
	contentId,
	hasSSRConfigInSSRModeOrHydration,
	isFixedWidthImageOption,
	smartSpaceOverviewHeight,
	isAIGenerated,
	isRoundedCorners,
}: MediaCoverPictureProps) => {
	// For AI-generated images, always use the editor media config instead of the SSR config.
	// The SSR config uses an initialAuth token that was generated at page load time and may not
	// have access to files created after page load (like AI-generated images uploaded by the
	// Convo AI backend service). The editor media config has a dynamic authProvider that fetches
	// fresh tokens, ensuring the newly created file can be resolved.
	const useSSRConfig = hasSSRConfigInSSRModeOrHydration && !isAIGenerated;
	const mediaClientConfig = useSSRConfig
		? ssrConfig && ssrConfig.config
		: mediaConfig && {
				authProvider: mediaConfig.authProvider,
			};

	if (mediaClientConfig) {
		return (
			<MediaImage
				identifier={{
					mediaItemType: 'file',
					id: String(coverPicture.current.draft?.value.id),
					collectionName: useSSRConfig ? `contentId-${contentId}` : mediaConfig?.collection,
				}}
				mediaClientConfig={mediaClientConfig}
				//force rerender image on id change until https://product-fabric.atlassian.net/browse/CXP-2899 is fixed
				key={String(coverPicture.current.draft?.value.id)}
				apiConfig={{
					allowAnimated: true,
					mode: 'full-fit' as const,
					upscale: false,
				}}
				ssr={useSSRConfig ? ssrConfig?.mode : undefined}
			>
				{({ data, loading, error }) => {
					if (loading) {
						// TODO: Leverage coverPictureState instead to determine if the image was AI generated
						// https://hello.jira.atlassian.cloud/browse/CCI-12712
						if (
							isAIGenerated &&
							(isExperimentEnabled('cc_page_experiences_ai_header_image_generation') ||
								isExperimentEnabled('cc_page_experiences_aifc_auto_generated_header_img'))
						) {
							return null;
						}

						return (
							<LoadingDiv
								dynamicCoverImageHeight={dynamicCoverImageHeight}
								smartSpaceOverviewHeight={smartSpaceOverviewHeight}
							/>
						);
					}
					if (!data) {
						return null;
					}
					if (error) {
						showErrorFlag();
					}
					return (
						<ImageWrapper
							data-testid="actual-cover-picture"
							id="actual-cover-picture"
							src={data.src}
							coverPicturePosition={coverPicture.current.draft?.value.position}
							isFixedWidthImageOption={isFixedWidthImageOption}
							smartSpaceOverviewHeight={smartSpaceOverviewHeight}
							isRoundedCorners={isRoundedCorners}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
							className="cover-picture-wrapper-dangerously-set-inner-html"
						/>
					);
				}}
			</MediaImage>
		);
	}
};
